// Расчёты «Остатков» на клиенте: строки аптек по выбранным товарам, фильтры, сортировка, сводка.
// Фильтры повторяют серверные (lib/remainsView.js), чтобы выгрузка совпадала с экраном
import { BucketKey } from '../OnlineJournal/types';
import { PharmacyProduct, RemainsDataStats, RemainsPharmacy, RemainsProduct, SearchResponse, StockExpiry } from './remainsApi';

export interface LatLng { lat: number; lng: number }

/** Точка отсчёта расстояний: моё местоположение или выбранная аптека */
export interface Origin extends LatLng {
  label: string;
  /** Аптека-точка отсчёта (нет - местоположение оператора) */
  guid: string | null;
}

export type MissingFilter = '' | 'hours' | 'phone';

export interface PharmacyFilterState {
  region: string;
  openNow: boolean;
  missing: MissingFilter;
  /** Радиус от точки отсчёта, км: null - без ограничения */
  radius: number | null;
}

export const EMPTY_PHARMACY_FILTERS: PharmacyFilterState = { region: '', openNow: false, missing: '', radius: null };

export type PharmacySortKey = 'num' | 'positions' | 'units' | 'amount' | 'distance' | 'expiry';
export type SortDir = 'asc' | 'desc';
export interface PharmacySort { key: PharmacySortKey; dir: SortDir }

export const DEFAULT_PHARMACY_SORT: PharmacySort = { key: 'units', dir: 'desc' };

/** Первый клик по заголовку: количества - по убыванию, номер, расстояние и срок - по возрастанию */
export const PHARMACY_DESC_FIRST: PharmacySortKey[] = ['positions', 'units', 'amount'];

export type NearestExpiry = NonNullable<StockExpiry['nearest']>;

/** Аптека в таблице: итоги только по выбранным товарам (ничего не выбрано - по всем найденным) */
export interface PharmacyRow {
  pharmacy: RemainsPharmacy;
  positions: number;
  units: number;
  /** null - нет права на цены */
  amount: number | null;
  /** Ближайший срок среди товаров аптеки */
  nearest: NearestExpiry | null;
  expiredQty: number;
  /** Расстояние от точки отсчёта, км: null - нет точки или координат */
  distance: number | null;
}

/** Товар в списке найденных: итоги по аптекам зоны */
export interface ProductRow {
  product: RemainsProduct;
  pharmacies: number;
  units: number;
  amount: number | null;
  nearest: NearestExpiry | null;
  expiredQty: number;
}

const EARTH_RADIUS_KM = 6371;
const toRad = (deg: number): number => (deg * Math.PI) / 180;

/** Расстояние по прямой, км - та же формула гаверсинусов, что и на сервере */
export function distanceKm(a: LatLng, b: LatLng): number {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h = Math.pow(Math.sin(dLat / 2), 2) + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.pow(Math.sin(dLng / 2), 2);
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

export const hasCoords = (p: { lat: number | null; lng: number | null }): p is { lat: number; lng: number } => (
  p.lat !== null && p.lng !== null
);

const round2 = (v: number): number => Math.round(v * 100) / 100;

const earlier = (a: NearestExpiry | null, b: NearestExpiry | null): NearestExpiry | null => {
  if (!a) return b;
  if (!b) return a;
  return b.date < a.date ? b : a;
};

/**
 * Строки аптек по ответу поиска. selected - выбранные товары (пусто - все найденные):
 * аптеки без остатка выбранных товаров в таблицу не попадают.
 */
export function buildPharmacyRows(data: SearchResponse, selected: string[], origin: Origin | null, withSums: boolean): PharmacyRow[] {
  const picked = selected.length ? new Set(selected) : null;
  const rows = new Map<string, PharmacyRow>();
  data.pharmacies.forEach(p => {
    rows.set(p.guid, {
      pharmacy: p,
      positions: 0,
      units: 0,
      amount: withSums ? 0 : null,
      nearest: null,
      expiredQty: 0,
      distance: origin && hasCoords(p) ? distanceKm(origin, p) : null
    });
  });
  data.stocks.forEach(s => {
    if (picked && !picked.has(s.zid)) return;
    const row = rows.get(s.guid);
    if (!row) return;
    row.positions += 1;
    row.units = round2(row.units + s.qty);
    if (row.amount !== null) row.amount = round2(row.amount + (s.sum || 0));
    if (s.expiry) {
      row.nearest = earlier(row.nearest, s.expiry.nearest);
      row.expiredQty += s.expiry.expiredQty;
    }
  });
  return Array.from(rows.values()).filter(r => r.positions > 0);
}

/** Итоги по каждому найденному товару: allowed - аптеки, прошедшие фильтры списка (null - все аптеки зоны) */
export function buildProductRows(data: SearchResponse, withSums: boolean, allowed: Set<string> | null): ProductRow[] {
  const rows = new Map<string, ProductRow>();
  data.products.forEach(p => {
    rows.set(p.zid, { product: p, pharmacies: 0, units: 0, amount: withSums ? 0 : null, nearest: null, expiredQty: 0 });
  });
  data.stocks.forEach(s => {
    const row = rows.get(s.zid);
    if (!row || (allowed && !allowed.has(s.guid))) return;
    row.pharmacies += 1;
    row.units = round2(row.units + s.qty);
    if (row.amount !== null) row.amount = round2(row.amount + (s.sum || 0));
    if (s.expiry) {
      row.nearest = earlier(row.nearest, s.expiry.nearest);
      row.expiredQty += s.expiry.expiredQty;
    }
  });
  return Array.from(rows.values());
}

/** Найденные товары в одной аптеке (для карточки): по выбранным товарам, если выбор есть */
export function pharmacyStocks(data: SearchResponse, guid: string, selected: string[]): PharmacyProduct[] {
  const picked = selected.length ? new Set(selected) : null;
  const products = new Map<string, RemainsProduct>();
  data.products.forEach(p => products.set(p.zid, p));
  const out: PharmacyProduct[] = [];
  data.stocks.forEach(s => {
    const p = products.get(s.zid);
    if (s.guid !== guid || !p || (picked && !picked.has(s.zid))) return;
    out.push({ ...p, qty: s.qty, expiry: s.expiry, price: s.price, sum: s.sum });
  });
  return out.sort((a, b) => b.qty - a.qty || a.name.localeCompare(b.name, 'ru'));
}

/** Фильтры списка аптек - как matchesFilters на сервере (телефон - только при праве на контакты) */
export function filterPharmacyRows(rows: PharmacyRow[], f: PharmacyFilterState, origin: Origin | null, withContacts: boolean): PharmacyRow[] {
  const radius = origin && f.radius && f.radius > 0 ? f.radius : null;
  return rows.filter(({ pharmacy: p, distance }) => {
    if (f.region && String(p.region || '') !== f.region) return false;
    if (f.openNow && p.open !== true) return false;
    if (f.missing === 'hours' && p.hours) return false;
    if (f.missing === 'phone' && withContacts && p.phone) return false;
    if (radius !== null && (distance === null || distance > radius)) return false;
    return true;
  });
}

/** Номер аптеки для сортировки: «1/7» → 1.007, склады и аптеки без номера - в конце */
function numKey(p: RemainsPharmacy): number {
  const m = p.num ? /^(\d+)(?:\/(\d+))?/.exec(p.num) : null;
  if (!m) return Number.MAX_SAFE_INTEGER;
  return Number(m[1]) + (m[2] ? Number(m[2]) / 1000 : 0);
}

function sortValue(r: PharmacyRow, key: PharmacySortKey): number | null {
  switch (key) {
    case 'num': return numKey(r.pharmacy);
    case 'positions': return r.positions;
    case 'units': return r.units;
    case 'amount': return r.amount;
    case 'distance': return r.distance;
    case 'expiry': return r.nearest ? r.nearest.daysLeft : null;
    default: return null;
  }
}

/** Сортировка: пустые значения (нет расстояния, срока) - всегда в конце, при равенстве - по номеру аптеки */
export function sortPharmacyRows(rows: PharmacyRow[], sort: PharmacySort): PharmacyRow[] {
  const sign = sort.dir === 'asc' ? 1 : -1;
  return rows.slice().sort((a, b) => {
    const va = sortValue(a, sort.key);
    const vb = sortValue(b, sort.key);
    if (va === null && vb !== null) return 1;
    if (vb === null && va !== null) return -1;
    if (va !== null && vb !== null && va !== vb) return (va - vb) * sign;
    return numKey(a.pharmacy) - numKey(b.pharmacy) || a.pharmacy.name.localeCompare(b.pharmacy.name, 'ru');
  });
}

export type ProductSortKey = 'name' | 'pharmacies' | 'units' | 'amount' | 'expiry';
export interface ProductSort { key: ProductSortKey; dir: SortDir }

export const DEFAULT_PRODUCT_SORT: ProductSort = { key: 'units', dir: 'desc' };
export const PRODUCT_DESC_FIRST: ProductSortKey[] = ['pharmacies', 'units', 'amount'];

function productValue(r: ProductRow, key: ProductSortKey): number | null {
  switch (key) {
    case 'pharmacies': return r.pharmacies;
    case 'units': return r.units;
    case 'amount': return r.amount;
    case 'expiry': return r.nearest ? r.nearest.daysLeft : null;
    default: return null;
  }
}

/** Товары: папка 1С (folder) - фильтр (null - все папки, '' - без папки); без остатка в отфильтрованных аптеках - в конце */
export function sortProductRows(rows: ProductRow[], folder: string | null, sort: ProductSort): ProductRow[] {
  const sign = sort.dir === 'asc' ? 1 : -1;
  const list = folder === null ? rows : rows.filter(r => (r.product.folder || '') === folder);
  return list.slice().sort((a, b) => {
    if (sort.key === 'name') return a.product.name.localeCompare(b.product.name, 'ru') * sign;
    const va = productValue(a, sort.key);
    const vb = productValue(b, sort.key);
    if (va === null && vb !== null) return 1;
    if (vb === null && va !== null) return -1;
    if (va !== null && vb !== null && va !== vb) return (va - vb) * sign;
    return a.product.name.localeCompare(b.product.name, 'ru');
  });
}

export interface PharmacySummary {
  pharmacies: number;
  open: number;
  closed: number;
  noHours: number;
  noPhone: number;
  positions: number;
  units: number;
  amount: number | null;
  nearest: NearestExpiry | null;
  expiredQty: number;
  /** Ближайшая аптека к точке отсчёта */
  closest: PharmacyRow | null;
}

/** Сводка по отфильтрованным аптекам */
export function summarize(rows: PharmacyRow[], withSums: boolean): PharmacySummary {
  const s: PharmacySummary = {
    pharmacies: rows.length, open: 0, closed: 0, noHours: 0, noPhone: 0, positions: 0, units: 0,
    amount: withSums ? 0 : null, nearest: null, expiredQty: 0, closest: null
  };
  rows.forEach(r => {
    if (r.pharmacy.open === true) s.open += 1;
    if (r.pharmacy.open === false) s.closed += 1;
    if (!r.pharmacy.hours) s.noHours += 1;
    if (!r.pharmacy.phone) s.noPhone += 1;
    s.positions += r.positions;
    s.units = round2(s.units + r.units);
    if (s.amount !== null) s.amount = round2(s.amount + (r.amount || 0));
    s.nearest = earlier(s.nearest, r.nearest);
    s.expiredQty += r.expiredQty;
    if (r.distance !== null && (!s.closest || (s.closest.distance as number) > r.distance)) s.closest = r;
  });
  return s;
}

export interface Option { value: string; label: string; count: number }

/** Значения для фильтра с количеством: регионы аптек, папки товаров */
export function countOptions(values: Array<string | null>, emptyLabel: string): Option[] {
  const counts = new Map<string, number>();
  values.forEach(v => counts.set(v || '', (counts.get(v || '') || 0) + 1));
  const out: Option[] = [];
  counts.forEach((count, value) => out.push({ value, label: value || emptyLabel, count }));
  return out.sort((a, b) => (a.value ? 0 : 1) - (b.value ? 0 : 1) || a.label.localeCompare(b.label, 'ru'));
}

/** Корзина срока для цвета: сервер отдаёт ключ, без даты - «не указан» */
export const bucketKeyOf = (n: NearestExpiry | null): BucketKey => (n && n.bucket ? n.bucket : 'unknown');

/** Расстояние: до километра - метрами, дальше - км с одной цифрой */
export function fmtDistance(km: number | null): string {
  if (km === null) return '-';
  if (km < 1) return `${Math.max(10, Math.round((km * 1000) / 10) * 10)} м`;
  return `${km.toLocaleString('ru-RU', { maximumFractionDigits: km < 10 ? 1 : 0 })} км`;
}

/** Через сколько часов без обновления данные 1С считаются устаревшими (плановое обновление - каждые 30 мин) */
const STALE_HOURS = 3;

export interface Freshness {
  /** Время обновления по Алматы: '27.10.2026, 14:30' */
  updated: string | null;
  ageLabel: string | null;
  stale: boolean;
}

/** Свежесть данных 1С: lastUpdated приходит в UTC, показываем по Алматы */
export function freshnessOf(stats: RemainsDataStats | null, now = Date.now()): Freshness {
  const t = stats && stats.lastUpdated ? Date.parse(stats.lastUpdated) : NaN;
  if (!isFinite(t)) return { updated: null, ageLabel: null, stale: true };
  const updated = new Date(t).toLocaleString('ru-RU', {
    timeZone: 'Asia/Almaty', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });
  const minutes = Math.max(0, Math.round((now - t) / 60000));
  let ageLabel: string;
  if (minutes < 1) ageLabel = 'только что';
  else if (minutes < 60) ageLabel = `${minutes} мин назад`;
  else if (minutes < 48 * 60) ageLabel = `${Math.floor(minutes / 60)} ч назад`;
  else ageLabel = `${Math.floor(minutes / 1440)} дн. назад`;
  return { updated, ageLabel, stale: minutes > STALE_HOURS * 60 };
}

/** Ссылки на внешние карты по координатам (как в карточке сотрудника) */
export function mapLinks(p: LatLng): Array<{ label: string; href: string }> {
  return [
    { label: '2GIS', href: `https://2gis.kz/geo/${p.lng},${p.lat}` },
    { label: 'Яндекс', href: `https://yandex.kz/maps/?ll=${p.lng},${p.lat}&z=16&pt=${p.lng},${p.lat}` },
    { label: 'Google', href: `https://www.google.com/maps?q=${p.lat},${p.lng}` }
  ];
}

/** Маршрут во внешних картах от точки отсчёта до аптеки (на машине; вид транспорта меняется уже в картах) */
export function routeLinks(from: LatLng, to: LatLng): Array<{ label: string; href: string }> {
  return [
    // 2GIS сам находит город по координатам и переходит на /<город>/directions/...
    { label: '2GIS', href: `https://2gis.kz/directions/points/${from.lng},${from.lat}%7C${to.lng},${to.lat}` },
    { label: 'Яндекс', href: `https://yandex.kz/maps/?rtext=${from.lat},${from.lng}~${to.lat},${to.lng}&rtt=auto` },
    { label: 'Google', href: `https://www.google.com/maps/dir/?api=1&origin=${from.lat},${from.lng}&destination=${to.lat},${to.lng}&travelmode=driving` }
  ];
}
